import { themeGet } from "@styled-system/theme-get";
import { COLLECTION_NODE_DISPLAY_MODE } from "src/components/NodeVisualizer/Nodes/CollectionNode/index";
import {
  isRealTimeAllocationsEnabled,
  isAddingMemberEnabled,
} from "src/util/allocations";
import { getGroupBadgeVariant, isGroupActive } from "src/util/group";
import { NODE_TYPES } from "src/components/NodeVisualizer/consts";
import { getTargetGroupAndFilter } from "../../../utils/getTargetGroupAndFilter";
import {
  getCreatableNodeTypes,
  createCollectionNode,
  createNode,
} from "./shared";
import getMembersCollectionLabel from "./getMembersCollectionLabel";

/** HELP-10386: Widen member grid when only one expanded team shares this parent; compact when siblings are expanded. */
export const getExpandedSiblingTeamCount = (state, team) => {
  const parentKey = team?.directParentId ?? null;
  const expanded = state?.expandedNodes ?? [];
  return expanded.filter((id) => {
    const t = state?.teams?.[id];
    return t && (t.directParentId ?? null) === parentKey;
  }).length;
};

export const getDirectMemberCollectionId = (teamId) => {
  return `${teamId}_direct_members`;
};

export const createMembersCollectionNode = ({
  group,
  isGroupedBySupplyOrDemand,
  contextualData = {},
  state = {},
  members = [],
}) => {
  const { groupTypes, theme, user, workspace, allocationProject } =
    contextualData;
  const groupType = groupTypes[group.type];
  const getColor = (color) => themeGet(`colors.${color}`)({ theme });
  const isUnallocatedCollection = group.id !== "unallocated";

  const variant = groupType ? getGroupBadgeVariant(group, groupTypes) : "grey";

  const borderColor = getColor(`${variant}40`);
  const labelColor = getColor(variant);

  const { filter, targetGroup, filterGroup } = getTargetGroupAndFilter({
    state,
    collectionGroup: group,
    groupTypes,
  });

  const allocationsEnabled =
    isRealTimeAllocationsEnabled({
      user,
      workspace,
      allocationProject,
    }) && isAddingMemberEnabled(workspace, targetGroup);

  const canAllocateMembers = allocationsEnabled && isGroupActive(targetGroup);

  const creatableNodeTypes = getCreatableNodeTypes({
    groupType,
    isGroupedBySupplyOrDemand,
    group,
    canAllocateMembers,
  });

  const items = members.map((member) =>
    createNode(member.aggregateId, member, NODE_TYPES.PERSON_NODE)
  );

  // not ideal, but don't have directMemberCount when supply/demand grouping
  const itemsMax = group.directMemberCount ?? items.length ?? 0;
  const itemsCount = items.length ?? 0;

  const memberGridLayout =
    isGroupedBySupplyOrDemand || !group?.id
      ? undefined
      : getExpandedSiblingTeamCount(state, group) <= 1
        ? "spacious"
        : "compact";

  const collectionData = {
    groupId: group.id,
    items,
    itemsCount,
    itemsMax,
    itemsLoading: group.membersLoadingCount ?? 0,
    borderStyle:
      isGroupedBySupplyOrDemand && isUnallocatedCollection
        ? `2px solid ${borderColor}`
        : `2px solid ${getColor("greyLight")}`,
    labelColor: isGroupedBySupplyOrDemand ? labelColor : getColor("grey"),
    displayMode: isGroupedBySupplyOrDemand
      ? COLLECTION_NODE_DISPLAY_MODE.SINGLE_COLUMN
      : null,
    variant,
    creatableNodeTypes,
    contextForCreation:
      creatableNodeTypes.filter(({ canCreate }) => canCreate).length === 0
        ? null
        : { team: group, filter, targetGroup, filterGroup },
    loadingType: NODE_TYPES.PERSON_NODE,
  };

  if (memberGridLayout) {
    collectionData.memberGridLayout = memberGridLayout;
  }

  return createCollectionNode(
    getDirectMemberCollectionId(group.id),
    getMembersCollectionLabel({
      group,
      groupType,
      itemCount: itemsMax,
      variant,
      isGroupedBySupplyOrDemand,
    }),
    collectionData
  );
};
